import { useState } from "react";
import axios from "axios";
import { API, getAuthToken } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Download } from "lucide-react";

const AVAILABLE_FIELDS = [
  { id: 'quotation_id', label: 'Quotation ID' },
  { id: 'order_date', label: 'Order Date' },
  { id: 'seller_name', label: 'Seller Name' },
  { id: 'seller_code', label: 'Seller Code' },
  { id: 'quotation_type', label: 'Quotation Type' },
  { id: 'product_name', label: 'Product Name' },
  { id: 'sku_id', label: 'SKU ID' },
  { id: 'brand', label: 'Brand' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'mrp', label: 'MRP' },
  { id: 'discount', label: 'Discount %' },
  { id: 'invoice_value', label: 'Invoice Value' },
  { id: 'item_type', label: 'Type (Sale/Freebie)' },
  { id: 'status', label: 'Status' },
  { id: 'invoice_number', label: 'Invoice Number' },
  { id: 'invoice_date', label: 'Invoice Date' },
  { id: 'dispatch_date', label: 'Dispatch Date' },
  { id: 'total_invoice_value', label: 'Total Invoice Value' },
  { id: 'effective_discount', label: 'Effective Discount %' }
];

export default function AdvancedExportModal({ open, onClose }) {
  const [exporting, setExporting] = useState(false);
  const [dateType, setDateType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedFields, setSelectedFields] = useState(AVAILABLE_FIELDS.map(f => f.id));
  const [selectAll, setSelectAll] = useState(true);

  const handleToggleField = (fieldId) => {
    setSelectedFields(prev =>
      prev.includes(fieldId)
        ? prev.filter(f => f !== fieldId)
        : [...prev, fieldId]
    );
    setSelectAll(false);
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedFields(AVAILABLE_FIELDS.map(f => f.id));
    } else {
      setSelectedFields([]);
    }
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      toast.error("Please select at least one field");
      return;
    }

    setExporting(true);
    try {
      const payload = {
        date_type: dateType || null,
        date_from: dateFrom || null,
        date_to: dateTo || null,
        selected_fields: selectedFields
      };

      const response = await axios.post(
        `${API}/quotations/export/advanced`,
        payload,
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'quotations_advanced_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export completed successfully");
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error exporting data");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="advanced-export-title">Advanced Export</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* Date Range Section */}
          <div className="space-y-4 p-4 border rounded-lg">
            <h3 className="font-semibold">Date Range Filter (Optional)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date_type">Date Type</Label>
                <Select value={dateType} onValueChange={setDateType}>
                  <SelectTrigger id="date_type" data-testid="date-type-select">
                    <SelectValue placeholder="Select date type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="order_date">Order Date</SelectItem>
                    <SelectItem value="invoice_date">Invoice Date</SelectItem>
                    <SelectItem value="dispatch_date">Dispatch Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_from">From Date</Label>
                <Input
                  id="date_from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="date-from-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_to">To Date</Label>
                <Input
                  id="date_to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="date-to-input"
                />
              </div>
            </div>
          </div>

          {/* Field Selection Section */}
          <div className="space-y-4 p-4 border rounded-lg">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Select Fields to Export</h3>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="select_all"
                  checked={selectAll}
                  onCheckedChange={handleSelectAll}
                  data-testid="select-all-checkbox"
                />
                <Label htmlFor="select_all" className="cursor-pointer">Select All</Label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto">
              {AVAILABLE_FIELDS.map(field => (
                <div key={field.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={field.id}
                    checked={selectedFields.includes(field.id)}
                    onCheckedChange={() => handleToggleField(field.id)}
                    data-testid={`field-checkbox-${field.id}`}
                  />
                  <Label htmlFor={field.id} className="cursor-pointer text-sm">
                    {field.label}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">
              Selected: {selectedFields.length} of {AVAILABLE_FIELDS.length} fields
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={exporting}>
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting || selectedFields.length === 0}
              className="bg-accent hover:bg-accent/90 text-white"
              data-testid="export-button"
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? "Exporting..." : "Export to Excel"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
